
import Vue from 'vue';
import { Modal } from '@descomplica/vue-components';
import { CourseModel } from '@descomplica/dex-models';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { CourseCertificateModel } from '@/models/studentDocuments';
import {
  ShareAndDownloadStep,
  LinkedinStep,
  LoadingStep,
  SharedInLinkedinResult,
  CongratulationStep,
} from './partials';
import ConfettiExplosion from '@/components/animations/ConfettiExplosion.vue';
import { VIDEO_WATCHED_KEY } from './partials/CongratulationStep.vue';
import { LinkedinService } from '@/services/Linkedin';
import { listenLinkedinPopup, openLinkedinPopup, PostMediaCategory } from '@/helpers/linkedin';
import { getDexToken } from '@/helpers/get-tokens';
import { getDiffTime } from '@/helpers/date';
import SentryClient from '@/helpers/sentry';
import {
  TrackCertificateDefaultParams,
  TrackCertificateLinkedinShare,
} from '@/helpers/analytics/types';
import { PostgradAnalytics } from '@/helpers/analytics/postgradAnalytics';
import { PostgradEarlyGraduationTrackingInformation } from '@/models/postgradEarlyGraduation/PostgradEarlyGraduationModel';

enum CertificateModalSteps {
  CONGRATULATIONS = 'CONGRATULATIONS',
  SHARE_AND_DOWNLOAD = 'SHARE_AND_DOWNLOAD',
  LINKEDIN_MESSAGE = 'LINKEDIN_MESSAGE',
  LOADING = 'LOADING',
  CERTIFICATE_SHARED = 'CERTIFICATE_SHARED',
}

interface Data {
  CertificateModalSteps: typeof CertificateModalSteps;
  currentStep: CertificateModalSteps | null;
  linkedinPopup: Window | null;
  linkedinOAuthCode: string;
  sharedSuccessfully: boolean;
  showConfettiExplosionAnimation: boolean;
}

export default Vue.extend({
  components: {
    Modal,
    CongratulationStep,
    ConfettiExplosion,
    ShareAndDownloadStep,
    LinkedinStep,
    LoadingStep,
    SharedInLinkedinResult,
  },
  props: {
    showModal: {
      required: true,
      type: Boolean,
    },
    loadError: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      CertificateModalSteps,
      linkedinPopup: null,
      linkedinOAuthCode: '',
      sharedSuccessfully: true,
      currentStep:
        localStorage.getItem(VIDEO_WATCHED_KEY) || this.loadError
          ? CertificateModalSteps.SHARE_AND_DOWNLOAD
          : CertificateModalSteps.CONGRATULATIONS,
      showConfettiExplosionAnimation: false,
    };
  },
  computed: {
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    certificateByCourseId(): CourseCertificateModel | null {
      return this.$store.getters.certificateByCourseId(this.currentCourse.id);
    },
    expiredChimeraCourseById(): ChimeraCourseModel | null {
      return this.$store.getters.expiredChimeraCourseById(this.currentCourse.descomplicaCourseId);
    },
    linkedinPopupEventListener(): (this: Window, ev: MessageEvent<any>) => any {
      return (messageEvent: MessageEvent) =>
        listenLinkedinPopup({
          messageEvent,
          callback: this.setLinkedinOAuthCode,
          linkedinPopup: this.linkedinPopup!,
        });
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
    defaultTrackingProps(): TrackCertificateDefaultParams {
      return {
        student_certificate: true,
        category: this.currentCourse.descomplicaCategorySlug,
        course: this.currentCourse.name,
        has_course_expired: !!this.expiredChimeraCourseById,
        ...this.daysReferenceToContractExpiration,
        ...(this.certificateByCourseId && {
          organization_name: this.certificateByCourseId.certificateOrganizationName,
        }),
        ...this.earlyGraduationTrackingProps,
      };
    },
    daysReferenceToContractExpiration(): { [value: string]: number } {
      const todayDate = new Date();
      if (this.currentChimeraCourse?.contractByUser) {
        const diffInTime = getDiffTime(
          this.currentChimeraCourse.contractByUser.expirationDate,
          todayDate,
        ).days;
        return { days_before_contract_expiration: diffInTime };
      }

      const { mostRecentExpiredCourseContract } = this.expiredChimeraCourseById!;
      if (!mostRecentExpiredCourseContract) return {};

      const diffInTime = getDiffTime(
        todayDate,
        mostRecentExpiredCourseContract.expirationDate,
      ).days;
      return { days_after_contract_expiration: diffInTime };
    },
  },
  watch: {
    currentStep: {
      handler(value) {
        if (value === CertificateModalSteps.SHARE_AND_DOWNLOAD && !this.loadError)
          this.showConfettiExplosionAnimation = true;
      },
      immediate: true,
    },
  },
  mounted() {
    this.trackModalViewed();
  },
  beforeDestroy() {
    window.removeEventListener('message', this.linkedinPopupEventListener);
  },
  methods: {
    viewCertificate({ percentage }: { percentage: number }): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        step: this.currentStep,
        congratulation_video_percentage_watched: percentage,
        ...this.defaultTrackingProps,
      });

      this.loadNextStep();
    },
    openLinkedinPopup(): void {
      this.linkedinPopup = openLinkedinPopup();

      window.addEventListener('message', this.linkedinPopupEventListener, false);

      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        error: this.loadError,
        step: this.currentStep,
        ...this.defaultTrackingProps,
        on_share_certificate: true,
      });
    },
    setLinkedinOAuthCode(code: string) {
      this.linkedinOAuthCode = code;
      this.loadNextStep();
    },
    closeModal() {
      Analytics.trackEvent(EVENT_LABELS.modalClosed, {
        error: this.loadError,
        step: this.currentStep,
        ...this.defaultTrackingProps,
      });

      this.$emit('onClose');
    },
    loadNextStep(): void {
      const nextStepsMapper = {
        [CertificateModalSteps.CONGRATULATIONS]: CertificateModalSteps.SHARE_AND_DOWNLOAD,
        [CertificateModalSteps.SHARE_AND_DOWNLOAD]: CertificateModalSteps.LINKEDIN_MESSAGE,
        [CertificateModalSteps.LINKEDIN_MESSAGE]: CertificateModalSteps.LOADING,
        [CertificateModalSteps.LOADING]: CertificateModalSteps.CERTIFICATE_SHARED,
        [CertificateModalSteps.CERTIFICATE_SHARED]: null,
      };

      this.currentStep = nextStepsMapper[this.currentStep as keyof typeof CertificateModalSteps];
      this.trackModalViewed();
    },
    async shareCertificate(options: {
      content: string;
      visibility: string;
      hasChangedContent: boolean;
    }): Promise<void> {
      const dexToken = getDexToken();

      if (!dexToken || !this.linkedinOAuthCode || !this.certificateByCourseId) return;

      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        ...this.defaultTrackingProps,
        step: this.currentStep,
        visibility_option: options.visibility,
        has_changed_default_text: options.hasChangedContent,
        on_share_certificate: true,
      });

      this.loadNextStep();

      try {
        await LinkedinService.sharePost(dexToken, {
          content: options.content,
          imageSource: this.certificateByCourseId.socialVersionImageUrl,
          linkedinOAuthCode: this.linkedinOAuthCode,
          postCategory: PostMediaCategory.IMAGE,
          visibility: options.visibility,
        });
        Analytics.trackEvent<TrackCertificateLinkedinShare>(
          EVENT_LABELS.postgradStudentDocumentsCertificateShareLinkedin,
          {
            ...this.defaultTrackingProps,
            visibility_option: options.visibility,
            has_changed_default_text: options.hasChangedContent,
          },
        );
      } catch (error: any) {
        this.sharedSuccessfully = false;
        const trackingParams = {
          ...this.defaultTrackingProps,
          visibility_option: options.visibility,
          has_changed_default_text: options.hasChangedContent,
        };

        SentryClient.captureError(error);
        PostgradAnalytics.trackPegasusError({
          error,
          eventName: EVENT_LABELS.postgradStudentDocumentsCertificateShareLinkedinError,
          trackingParams,
        });
      }

      this.loadNextStep();
    },
    trackModalViewed(): void {
      Analytics.trackEvent(EVENT_LABELS.modalViewed, {
        error: this.loadError || !this.sharedSuccessfully,
        step: this.currentStep,
        ...this.defaultTrackingProps,
      });
    },
    hideConfettiExplosionAnimation(): void {
      this.showConfettiExplosionAnimation = false;
    },
  },
});
